import usePrivateAxios from '@/common/api/usePrivateAxios'
import { transformNewGeos } from '@/common/utils/geoTransformers'
import { getRailsUpdate } from '@/common/utils/getRailsUpdate'
import { mapToDeletion } from '@/common/utils/railsHelpers'
import { unFormatCnpj } from '@/common/utils/unFormatters'
import { PublicCompanyLeadsFunnelUpdateValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { PublicCompanyLeadData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { useMutation } from '@tanstack/react-query'

interface UseUpdatePublicCompanyLeadProps {
  id?: string
  publicCompanyLead?: PublicCompanyLeadData
  file?: Blob
  onSuccess: () => void
  onError: (error: any) => void
}

export const useUpdatePublicCompanyLead = ({
  id,
  publicCompanyLead,
  file,
  onSuccess,
  onError,
}: UseUpdatePublicCompanyLeadProps) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['updatePublicCompanyLead'],
    mutationFn: (data: PublicCompanyLeadsFunnelUpdateValidationSchema) => {
      const lead = {
        commercial_manager_id: data.commercial_manager?.value,
        commercial_manager_substitute_id: data.commercial_manager_substitute?.value || 'null',
        bidding_information_attributes: {
          contest_date: data.bidding_information?.contest_date,
          geo_geoables_attributes: getRailsUpdate(
            publicCompanyLead?.bidding_information?.geo_geoables || [],
            data?.bidding_information?.geo_geoables || [],
            (geo) => geo.geo.id,
            (geo) => geo.value || '',
            mapToDeletion,
            transformNewGeos,
          ),
          government_segment: data.bidding_information?.government_segment?.value,
          estimated_equipment_total: data.bidding_information?.estimated_equipment_total,
          minimum_establishments_in_region: data.bidding_information?.minimum_establishments_in_region,
          notice_number: data.bidding_information?.notice_number,
          object_of_the_bidding: data.bidding_information?.object_of_the_bidding,
        },
        fiscal_document_information_attributes: {
          ...data.fiscal_document_information,
          fiscal_document_kind: data.fiscal_document_information?.fiscal_document_kind?.value || undefined,
          seller_cnpj: data.fiscal_document_information?.seller_cnpj
            ? unFormatCnpj(data.fiscal_document_information.seller_cnpj)
            : undefined,
        },
        general_information_attributes: {
          ...data.general_information,
          company_logo: file ? file : undefined,
        },
        address_attributes: {
          ...data.address_attributes,
          city_id: data.address_attributes?.city?.id,
        },
        printer_machine_id: data.printer_machine_id?.value || undefined,
        observations: data.observations
      }

      return privateAxios.putForm(`/funnel/public/leads/${id}`, {
        lead,
      })
    },
    onSuccess,
    onError,
  })
}
